import Link from "next/link";
import Image from "next/image";
import { CheckCircle2, ArrowRight } from "lucide-react";
import type { Metadata } from "next";

export const metadata: Metadata = {
  title: "Hire Tradies — $50 Flat Fee | RateRight",
  description:
    "Hire skilled construction workers for a one-time $50 fee. No agency markup, no ongoing costs. Post a job in 60 seconds.",
  openGraph: {
    title: "Hire Tradies — $50 Flat Fee | RateRight",
    description: "Hire construction workers for $50. Once. Keep them forever.",
    type: "website",
  },
};

export default async function HirePage({
  searchParams,
}: {
  searchParams: Promise<{ [key: string]: string | string[] | undefined }>;
}) {
  const params = await searchParams;
  const city = typeof params.city === "string" ? params.city : "Australia";
  const source = typeof params.source === "string" ? params.source : "qr";
  const variant = typeof params.variant === "string" ? params.variant : "default";

  const displayCity = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();

  const signupUrl = `/contractor/signup?utm_source=${encodeURIComponent(source)}&utm_medium=qr&utm_campaign=contractor-acquisition&utm_content=${encodeURIComponent(variant)}&city=${encodeURIComponent(city)}`;

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
{/* Header */}      <header className="border-b border-gray-800">        <div className="mx-auto flex h-12 max-w-md items-center justify-center px-6">          <Image            src="/logo_white.svg"            alt="RateRight"            width={120}            height={34}            className="h-6 w-auto"            priority          />        </div>      </header>
      {/* Hero */}
      <section className="flex-1 flex flex-col justify-center px-6 py-16">
        <div className="mx-auto w-full max-w-md">
          <div className="inline-flex items-center gap-2 rounded-full bg-green-500/10 px-4 py-1.5 text-sm font-medium text-green-400">
            <span className="h-2 w-2 rounded-full bg-green-400" />
            300+ contractors in {displayCity}
          </div>

          <h1 className="mt-6 text-[2rem] font-extrabold leading-[1.1] tracking-tight sm:text-5xl">
            Hire Skilled Tradies.{" "}
            <span className="text-green-400">$50. Once.</span>{" "}
            <span className="text-gray-400">Keep Them.</span>
          </h1>

          <ul className="mt-8 space-y-4">
            <Bullet text="No agency markup. $50 flat — hire for a day or a year." />
            <Bullet text="Workers in your area. Matched by trade, distance, and ratings." />
            <Bullet text="Post in 60 seconds. Get matches in minutes." />
          </ul>

          <Link href={signupUrl} className="mt-10 block">
            <span className="flex h-14 w-full items-center justify-center gap-3 rounded-xl bg-green-500 text-base font-bold text-white shadow-lg shadow-green-500/25 transition-colors hover:bg-green-600 active:bg-green-700 sm:w-80">
              Post a Job — $50 Flat Fee
              <ArrowRight className="h-5 w-5" />
            </span>
          </Link>

          <p className="mt-4 text-sm text-gray-500">
            Free to post. Only pay when you hire.
          </p>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="border-t border-gray-800 px-6 py-5">
        <div className="mx-auto max-w-md text-center">
          <p className="text-xs text-gray-600">
            &copy; 2026 RateRight Pty Ltd &middot; ABN 88 688 279 769
          </p>
          <div className="mt-2 flex items-center justify-center gap-4 text-xs text-gray-600">
            <Link href="/legal/terms-of-service" className="hover:text-gray-400 min-h-[44px] flex items-center">Terms</Link>
            <Link href="/legal/privacy-policy" className="hover:text-gray-400 min-h-[44px] flex items-center">Privacy</Link>
            <Link href="/faq" className="hover:text-gray-400 min-h-[44px] flex items-center">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Bullet({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
      <span className="text-base leading-relaxed text-gray-200">{text}</span>
    </li>
  );
}
